/**
 * ТАБЫ страницы комнаты: Участники · Медиа · Ссылки · Голосовые.
 *
 * Образец Антона 2026-09-10 (три скриншота Telegram): ряд табов, под ними
 * содержимое. Медиа — сетка превью с группировкой по месяцам («AUGUST 2026»)
 * и длительностью на видео. Ссылки — карточки с заголовком, описанием и
 * миниатюрой. Голосовые — свой таб.
 *
 * ЧЕСТНОСТЬ ЧИСЕЛ. В образце написано «23 media», потому что Telegram знает
 * все вложения чата. Мы знаем только просмотренную часть истории: Matrix не
 * отдаёт список вложений комнаты, его собирают проходом по таймлайну. Поэтому
 * здесь пишется «найдено N» и есть кнопка «Искать глубже», а не выдуманное
 * «всего N». Пустой таб честно говорит, что просмотрено N сообщений и
 * ничего не найдено, — это не то же самое, что «вложений нет».
 */
import { useEffect, useState } from "react";
import type { Messages } from "../i18n/messages";
type TKey = keyof Messages;
import { useLocale, useT, useTF } from "../i18n";
import type { MatrixClient, Room } from "matrix-js-sdk";

import { useAuthedMedia } from "../matrix/media";
import {
  fetchLinkPreview,
  groupByMonth,
  scanRoomAttachments,
  type Attachment,
  type LinkPreview,
  type RoomLink,
} from "../matrix/roomAttachments";
import { color, font, radius, space } from "./theme";

export type RoomTab = "members" | "media" | "files" | "links" | "voice";

const tabsOf = (t: (k: TKey) => string): { id: RoomTab; label: string }[] => [
  { id: "members", label: t("room.members") },
  { id: "media", label: t("profile.mediaTab") },
  // Отдельный таб (Антон 15:40 «добавить файлы»): раньше файлы висели
  // хвостом под сеткой медиа и терялись за картинками.
  { id: "files", label: t("profile.filesTab") },
  { id: "links", label: t("room.tabLinks") },
  { id: "voice", label: t("room.tabVoice") },
];

export function TabStrip({
  active,
  onSelect,
}: {
  active: RoomTab;
  onSelect: (t: RoomTab) => void;
}) {
  const t = useT();
  return (
    <div
      style={{
        display: "flex",
        borderBottom: `1px solid ${color.border}`,
        background: color.bg,
        position: "sticky",
        top: 0,
        zIndex: 1,
      }}
      role="tablist"
    >
      {tabsOf(t).map((tab) => {
        const on = tab.id === active;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => onSelect(tab.id)}
            data-testid={`room-tab-${tab.id}`}
            style={{
              flex: "1 1 0",
              padding: "10px 4px 8px",
              border: "none",
              background: "transparent",
              cursor: "pointer",
              fontSize: font.small,
              fontWeight: on ? 700 : 400,
              color: on ? color.accent : color.textMuted,
              transition: "color 0.12s, background 0.12s",
            }}
            onMouseEnter={(e) => {
              // Неактивный таб при наведении темнеет (образец 10.09).
              if (!on) e.currentTarget.style.color = color.text;
              else e.currentTarget.style.background = color.hover;
            }}
            onMouseLeave={(e) => {
              if (!on) e.currentTarget.style.color = color.textMuted;
              else e.currentTarget.style.background = "transparent";
            }}
          >
            {/* Подчёркивание активного таба — 3px и ПО ШИРИНЕ ТЕКСТА,
                а не по ширине ячейки (образец Антона 10.09): черта через
                всю кнопку читалась как разделитель секций, а не как маркер
                выбранного слова. Раньше здесь был borderBottom 2px на всю
                кнопку — заменён на borderBottom у внутреннего span. */}
            <span
              style={{
                display: "inline-block",
                paddingBottom: 5,
                borderBottom: on
                  ? `3px solid ${color.accent}`
                  : "3px solid transparent",
              }}
            >
              {tab.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

interface Scan {
  attachments: Attachment[];
  links: RoomLink[];
  hasMore: boolean;
  scanned: number;
}

/** Один проход по истории на всю панель: три таба читают один результат. */
export function useRoomAttachments(client: MatrixClient, room: Room) {
  const [scan, setScan] = useState<Scan | null>(null);
  const [busy, setBusy] = useState(false);
  const [budget, setBudget] = useState(600);

  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    scanRoomAttachments(client, room, budget)
      .then((r) => {
        if (!cancelled) setScan(r);
      })
      .catch(() => {
        if (!cancelled) setScan({ attachments: [], links: [], hasMore: false, scanned: 0 });
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [client, room, budget]);

  return { scan, busy, deeper: () => setBudget((b) => b + 1200) };
}

function Deeper({
  scan,
  busy,
  onDeeper,
}: {
  scan: Scan;
  busy: boolean;
  onDeeper: () => void;
}) {
  const t = useT();
  const tfmt = useTF();
  return (
    <div style={{ padding: space.md, fontSize: font.small, color: color.textDim }}>
      {tfmt("room.scanCount", { count: scan.scanned })}{" "}
      {scan.hasMore ? (
        <button
          type="button"
          onClick={onDeeper}
          disabled={busy}
          data-testid="scan-deeper"
          style={{
            border: `1px solid ${color.border}`,
            background: color.bg,
            color: color.text,
            borderRadius: radius.sm,
            padding: "4px 8px",
            cursor: busy ? "default" : "pointer",
            fontSize: font.small,
          }}
          onMouseEnter={(e) => {
            if (!busy) e.currentTarget.style.background = color.hover;
          }}
          onMouseLeave={(e) => {
            if (!busy) e.currentTarget.style.background = color.bg;
          }}
        >
          {busy ? t("room.scanBusy") : t("room.scanDeeper")}
        </button>
      ) : (
        t("room.scanComplete")
      )}
    </div>
  );
}

function fmtDuration(ms?: number): string | null {
  if (!ms || ms <= 0) return null;
  const total = Math.round(ms / 1000);
  const m = Math.floor(total / 60);
  const sec = total % 60;
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

function Tile({ client, item }: { client: MatrixClient; item: Attachment }) {
  const { url } = useAuthedMedia(client, item.thumbMxc ?? item.mxc);
  const dur = fmtDuration(item.durationMs);
  return (
    <div
      style={{
        position: "relative",
        aspectRatio: "1 / 1",
        background: color.sidebarBg,
        overflow: "hidden",
        borderRadius: radius.sm,
      }}
      title={item.name}
      data-testid="media-tile"
    >
      {url ? (
        <img
          src={url}
          alt={item.name}
          loading="lazy"
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      ) : (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "grid",
            placeItems: "center",
            fontSize: 20,
            color: color.textDim,
          }}
        >
          {item.kind === "video" ? "▶" : "🖼"}
        </div>
      )}
      {dur && (
        <span
          style={{
            position: "absolute",
            left: 4,
            top: 4,
            background: "rgba(0,0,0,.65)",
            color: "#fff",
            fontSize: 11,
            padding: "1px 5px",
            borderRadius: radius.sm,
          }}
        >
          {dur}
        </span>
      )}
    </div>
  );
}

export function MediaTab({
  client,
  scan,
  busy,
  onDeeper,
}: {
  client: MatrixClient;
  scan: Scan;
  busy: boolean;
  onDeeper: () => void;
}) {
  const t = useT();
  const tfmt = useTF();
  const media = scan.attachments.filter((a) => a.kind === "image" || a.kind === "video");
  return (
    <div>
      <div style={{ padding: `${space.md}px ${space.md}px 0`, fontSize: font.small, color: color.textDim }}>
        {tfmt("room.mediaFound", { count: media.length })}
      </div>
      {groupByMonth(media).map(([label, items]) => (
        <div key={label}>
          <div
            style={{
              padding: `${space.md}px ${space.md}px 6px`,
              fontSize: font.small,
              color: color.textDim,
              letterSpacing: 0.4,
            }}
          >
            {label}
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 2,
              padding: `0 ${space.md}px`,
            }}
          >
            {items.map((a) => (
              <Tile key={a.eventId} client={client} item={a} />
            ))}
          </div>
        </div>
      ))}
      {media.length === 0 && !busy && (
        <EmptyState
          icon="🖼"
          text={t("ui.v_istorii_komnaty_ne_naydeno_kartinok_i")}
          hint={
            scan.hasMore
              ? t("ui.my_prosmotreli_tolko_chast_istorii_nazhm")
              : t("room.scanComplete")
          }
        />
      )}
      {busy && media.length === 0 && <SkeletonGrid />}
      <Deeper scan={scan} busy={busy} onDeeper={onDeeper} />
    </div>
  );
}

/** Человекочитаемый размер: 1.4 МБ вместо 1468006. */
function fmtSize(bytes: number | undefined, t: (k: TKey) => string): string | null {
  if (!bytes || bytes <= 0) return null;
  const u = [t("ui.b"), t("ui.kb"), t("ui.mb"), t("ui.gb")];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < u.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v >= 10 || i === 0 ? Math.round(v) : v.toFixed(1)} ${u[i]}`;
}

/** Иконка по расширению — быстрее читается, чем одинаковая скрепка у всех. */
function fileIcon(name: string): string {
  const ext = name.toLowerCase().split(".").pop() ?? "";
  if (["pdf"].includes(ext)) return "📕";
  if (["doc", "docx", "rtf", "odt"].includes(ext)) return "📘";
  if (["xls", "xlsx", "csv", "ods"].includes(ext)) return "📗";
  if (["ppt", "pptx"].includes(ext)) return "📙";
  if (["zip", "rar", "7z", "gz", "tar"].includes(ext)) return "🗜";
  if (["mp3", "wav", "ogg", "flac", "m4a"].includes(ext)) return "🎵";
  if (["txt", "md", "log"].includes(ext)) return "📄";
  return "📎";
}

function FileRow({ client, item }: { client: MatrixClient; item: Attachment }) {
  const t = useT();
  const tfmt = useTF();
  const locale = useLocale();
  // Скачивание идёт через авторизованный media-запрос: у нас Synapse с
  // authenticated media, прямая ссылка на mxc из браузера отдаст 401.
  const { url } = useAuthedMedia(client, item.mxc);
  const size = fmtSize(item.sizeBytes, t);
  const when = new Date(item.ts).toLocaleDateString(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  return (
    <a
      href={url ?? undefined}
      download={item.name}
      target="_blank"
      rel="noreferrer noopener"
      data-testid="file-row"
      style={{
        display: "flex",
        alignItems: "center",
        gap: space.md,
        padding: `9px ${space.md}px`,
        borderBottom: `1px solid ${color.border}`,
        textDecoration: "none",
        color: color.text,
        cursor: url ? "pointer" : "default",
        opacity: url ? 1 : 0.6,
      }}
      title={url ? tfmt("media.downloadNamed", { name: item.name }) : t("ui.gotovlyu_fayl")}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = color.hover;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
      }}
    >
      <span style={{ fontSize: 20, flex: "0 0 auto" }}>{fileIcon(item.name)}</span>
      <span style={{ minWidth: 0, flex: "1 1 auto" }}>
        <span
          style={{
            display: "block",
            fontSize: font.small,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {item.name}
        </span>
        <span style={{ display: "block", fontSize: 11, color: color.textDim }}>
          {[size, when].filter(Boolean).join(" · ")}
        </span>
      </span>
      <span style={{ color: color.accent, fontSize: 15, flex: "0 0 auto" }}>⤓</span>
    </a>
  );
}

export function FilesTab({
  client,
  scan,
  busy,
  onDeeper,
}: {
  client: MatrixClient;
  scan: Scan;
  busy: boolean;
  onDeeper: () => void;
}) {
  const t = useT();
  const tfmt = useTF();
  const files = scan.attachments.filter((a) => a.kind === "file");
  return (
    <div>
      <div style={{ padding: `${space.md}px ${space.md}px 0`, fontSize: font.small, color: color.textDim }}>
        {tfmt("room.mediaFound", { count: files.length })}
      </div>
      {groupByMonth(files).map(([label, items]) => (
        <div key={label}>
          <div
            style={{
              padding: `${space.md}px ${space.md}px 4px`,
              fontSize: font.small,
              color: color.textDim,
              letterSpacing: 0.4,
            }}
          >
            {label}
          </div>
          {items.map((f) => (
            <FileRow key={f.eventId} client={client} item={f} />
          ))}
        </div>
      ))}
      {files.length === 0 && !busy && (
        <EmptyState
          icon="📎"
          text={t("ui.v_istorii_komnaty_ne_naydeno_faylov")}
          hint={
            scan.hasMore
              ? t("ui.my_prosmotreli_tolko_chast_istorii_nazhm")
              : t("room.scanComplete")
          }
        />
      )}
      {busy && files.length === 0 && <SkeletonRows />}
      <Deeper scan={scan} busy={busy} onDeeper={onDeeper} />
    </div>
  );
}

function LinkCard({ client, link }: { client: MatrixClient; link: RoomLink }) {
  const [preview, setPreview] = useState<LinkPreview | null>(null);
  useEffect(() => {
    let dead = false;
    fetchLinkPreview(client, link.url).then((p) => {
      if (!dead) setPreview(p);
    });
    return () => {
      dead = true;
    };
  }, [client, link.url]);
  const { url: img } = useAuthedMedia(client, preview?.imageMxc ?? null);

  return (
    <a
      href={link.url}
      target="_blank"
      rel="noreferrer noopener"
      data-testid="link-card"
      style={{
        display: "flex",
        gap: space.md,
        padding: `10px ${space.md}px`,
        textDecoration: "none",
        color: color.text,
        borderBottom: `1px solid ${color.border}`,
        transition: "background 0.12s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = color.hover;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
      }}
    >
      {img && (
        <img
          src={img}
          alt=""
          style={{
            width: 44,
            height: 44,
            borderRadius: radius.sm,
            objectFit: "cover",
            flex: "0 0 auto",
          }}
        />
      )}
      <span style={{ minWidth: 0 }}>
        {preview?.title && (
          <span style={{ display: "block", fontWeight: 600, fontSize: font.small }}>
            {preview.title}
          </span>
        )}
        {preview?.description && (
          <span
            style={{
              display: "block",
              fontSize: font.small,
              color: color.textDim,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {preview.description}
          </span>
        )}
        <span
          style={{
            display: "block",
            fontSize: font.small,
            color: color.accent,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {link.url.replace(/^https?:\/\//, "")}
        </span>
      </span>
    </a>
  );
}

export function LinksTab({
  client,
  scan,
  busy,
  onDeeper,
}: {
  client: MatrixClient;
  scan: Scan;
  busy: boolean;
  onDeeper: () => void;
}) {
  const t = useT();
  const tfmt = useTF();
  return (
    <div>
      <div style={{ padding: `${space.md}px ${space.md}px 0`, fontSize: font.small, color: color.textDim }}>
        {tfmt("room.mediaFound", { count: scan.links.length })}
      </div>
      {scan.links.map((l) => (
        <LinkCard key={`${l.eventId}-${l.url}`} client={client} link={l} />
      ))}
      {scan.links.length === 0 && !busy && (
        <EmptyState
          icon="🔗"
          text={t("ui.v_istorii_komnaty_ne_naydeno_ssylok")}
          hint={
            scan.hasMore
              ? t("ui.my_prosmotreli_tolko_chast_istorii_nazhm")
              : t("room.scanComplete")
          }
        />
      )}
      {busy && scan.links.length === 0 && <SkeletonRows />}
      <Deeper scan={scan} busy={busy} onDeeper={onDeeper} />
    </div>
  );
}

export function VoiceTab({
  client,
  scan,
  busy,
  onDeeper,
}: {
  client: MatrixClient;
  scan: Scan;
  busy: boolean;
  onDeeper: () => void;
}) {
  const t = useT();
  const tfmt = useTF();
  const voice = scan.attachments.filter((a) => a.kind === "voice");
  return (
    <div>
      <div style={{ padding: `${space.md}px ${space.md}px 0`, fontSize: font.small, color: color.textDim }}>
        {tfmt("room.mediaFound", { count: voice.length })}
      </div>
      {voice.map((v) => (
        <VoiceRow key={v.eventId} client={client} item={v} />
      ))}
      {voice.length === 0 && !busy && (
        <EmptyState
          icon="🎤"
          text={t("ui.v_istorii_komnaty_ne_naydeno_golosovyh")}
          hint={
            scan.hasMore
              ? t("ui.my_prosmotreli_tolko_chast_istorii_nazhm")
              : t("room.scanComplete")
          }
        />
      )}
      {busy && voice.length === 0 && <SkeletonRows />}
      <Deeper scan={scan} busy={busy} onDeeper={onDeeper} />
    </div>
  );
}

function VoiceRow({ client, item }: { client: MatrixClient; item: Attachment }) {
  const { url } = useAuthedMedia(client, item.mxc);
  const dur = fmtDuration(item.durationMs);
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: space.md,
        padding: `8px ${space.md}px`,
        borderBottom: `1px solid ${color.border}`,
        transition: "background 0.12s",
      }}
      data-testid="voice-row"
      onMouseEnter={(e) => {
        e.currentTarget.style.background = color.hover;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
      }}
    >
      <span style={{ fontSize: 18 }}>🎤</span>
      <span style={{ flex: "1 1 auto", minWidth: 0 }}>
        {url ? (
          <audio controls src={url} style={{ width: "100%", height: 32 }} />
        ) : (
          <span style={{ fontSize: font.small, color: color.textDim }}>{item.name}</span>
        )}
      </span>
      {dur && <span style={{ fontSize: font.small, color: color.textDim }}>{dur}</span>}
    </div>
  );
}

/**
 * Пустое состояние таба: крупная иконка по центру + честная формулировка.
 *
 * Почему «в истории не найдено», а не «нет»: Matrix не отдаёт список
 * вложений комнаты, мы просматриваем таймлайн окном в сотни сообщений.
 * «Файлов нет» врало бы: их просто может не быть в ПРОСМОТРЕННОЙ части.
 * Под основной фразой — мелкая серая подсказка, что делать дальше
 * (кнопка «искать глубже» уже есть ниже, ей и указываем путь).
 */
function EmptyState({ icon, text, hint }: { icon: string; text: string; hint?: string }) {
  return (
    <div
      style={{
        padding: `${space.xl * 2}px ${space.xl}px`,
        textAlign: "center",
        color: color.textDim,
        fontSize: font.small,
      }}
      data-testid="tab-empty"
    >
      <div style={{ fontSize: 30, marginBottom: space.md, opacity: 0.5 }}>{icon}</div>
      <div style={{ color: color.text }}>{text}</div>
      {hint && (
        <div style={{ marginTop: space.sm, fontSize: font.micro, color: color.textDim }}>
          {hint}
        </div>
      )}
    </div>
  );
}

/** Скелетон сетки медиа: 3×3 серых квадрата. */
function SkeletonGrid() {
  return (
    <div style={{ padding: `0 ${space.md}px` }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 2,
        }}
      >
        {Array.from({ length: 9 }, (_, i) => (
          <div
            key={i}
            style={{
              aspectRatio: "1 / 1",
              background: color.hover,
              borderRadius: radius.sm,
              animation: "pulse 1.5s ease-in-out infinite",
            }}
          />
        ))}
      </div>
      <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }`}</style>
    </div>
  );
}

/** Скелетон строк: 5 серых полос. */
function SkeletonRows() {
  return (
    <div>
      {Array.from({ length: 5 }, (_, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            alignItems: "center",
            gap: space.md,
            padding: `10px ${space.md}px`,
            borderBottom: `1px solid ${color.border}`,
          }}
        >
          <div
            style={{
              width: 20,
              height: 20,
              borderRadius: radius.sm,
              background: color.hover,
              animation: "pulse 1.5s ease-in-out infinite",
            }}
          />
          <div style={{ flex: "1 1 auto" }}>
            <div
              style={{
                height: 14,
                width: "70%",
                background: color.hover,
                borderRadius: radius.sm,
                marginBottom: 4,
                animation: "pulse 1.5s ease-in-out infinite",
              }}
            />
            <div
              style={{
                height: 11,
                width: "40%",
                background: color.hover,
                borderRadius: radius.sm,
                animation: "pulse 1.5s ease-in-out infinite",
              }}
            />
          </div>
        </div>
      ))}
      <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }`}</style>
    </div>
  );
}
